/**
 * TasksLayout - Layout wrapper for External Tasks page
 *
 * Provides a consistent layout structure with:
 * - Top section: Title with vertical selector
 * - Bottom section: Content area
 *
 * Note: This layout is used only by ExternalTasksPage.
 * The main /tasks routes use TaskListView which has its own layout.
 */

import * as React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { TaskVerticalSwitcher } from '../tasks-datatable/components/TaskVerticalSwitcher';

interface TasksLayoutProps {
  children: React.ReactNode;
}

export const TasksLayout: React.FC<TasksLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Determine current vertical from URL/route
  // - External route (/external-tasks) -> 'external'
  // - Tasks route (/tasks) -> from param or default to 'all'
  const isExternalTaskMode = location.pathname.startsWith('/external-tasks');
  const currentVertical = isExternalTaskMode
    ? 'external'
    : searchParams.get('vertical') || 'all';

  return (
    <div
      className="flex flex-col gap-2 px-10 py-2 bg-[#FAFAFA] dark:bg-zinc-950 overflow-hidden"
      style={{ height: 'calc(100vh - 64px)', contain: 'layout style' }}
    >
      {/* Title Section: Page title with vertical selector */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <h1 className="font-serif text-[32px] font-medium leading-[40px] text-black dark:text-white">
          Tasks
        </h1>
        <TaskVerticalSwitcher currentVertical={currentVertical} />
      </div>

      {/* Content Section */}
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
};
